import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  PressReleaseAttachmentItem,
  PressReleaseChannelListItem,
  PressReleaseChannelRequest,
  PressReleasePostingItem,
  PressReleaseItem,
  PressReleaseRequest,
} from './pressReleaseTypes';

const BASE = '/api/press-releases';

/**
 * Serializes a list query. A facet is written whenever its key is PRESENT, empty string included —
 * an empty value is the reader saying "none of these", which matches no row.
 */
function serialize(query: ListQuery & { releaseStatus?: string }): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.releaseStatus !== undefined) params.set('releaseStatus', query.releaseStatus);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const pressReleasesApi = {
  list: (query: ListQuery & { releaseStatus?: string }, signal?: AbortSignal) =>
    api<PagedResult<PressReleaseItem>>(`${BASE}${serialize(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<PressReleaseItem>(`${BASE}/${id}`, { signal }),

  create: (body: PressReleaseRequest) => api<PressReleaseItem>(BASE, { method: 'POST', body }),

  update: (id: string, body: PressReleaseRequest) => api<PressReleaseItem>(`${BASE}/${id}`, { method: 'PUT', body }),

  publish: (id: string) => api<PressReleaseItem>(`${BASE}/${id}/publish`, { method: 'POST' }),

  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),

  // ── The channel checklist ──────────────────────────────────────────────────────────

  postings: (id: string, signal?: AbortSignal) => api<PressReleasePostingItem[]>(`${BASE}/${id}/postings`, { signal }),

  /** REPLACES the set of channels a published release is posted on. */
  savePostings: (id: string, channelIds: string[]) =>
    api<PressReleasePostingItem[]>(`${BASE}/${id}/postings`, { method: 'PUT', body: { channelIds } }),

  // ── Files ───────────────────────────────────────────────────────────────────────────

  attachments: (id: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<PressReleaseAttachmentItem>>(`${BASE}/${id}/attachments${serialize(query)}`, { signal }),

  /**
   * Files ONE file and the thumbnail the browser drew of it. Through `apiUpload` because the form
   * shows a REAL progress figure — `fetch` has no upload-progress event.
   */
  attach: (id: string, file: File, thumbnail: Blob | null, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    if (thumbnail) form.append('thumbnail', thumbnail, 'thumbnail.jpg');

    return apiUpload<PressReleaseAttachmentItem>(`${BASE}/${id}/attachments`, form, { onProgress });
  },

  /**
   * The file's bytes. A FETCH then an object URL, never an `<a href>` or a `<video src>` at the
   * endpoint: the token travels in a header no navigation sends, and the response is an attachment
   * on purpose.
   */
  download: (id: string, attachmentId: string, signal?: AbortSignal) =>
    apiBlob(`${BASE}/${id}/attachments/${attachmentId}`, { signal }),

  thumbnail: (id: string, attachmentId: string, signal?: AbortSignal) =>
    apiBlob(`${BASE}/${id}/attachments/${attachmentId}/thumbnail`, { signal }),

  removeAttachment: (id: string, attachmentId: string) =>
    api<void>(`${BASE}/${id}/attachments/${attachmentId}`, { method: 'DELETE' }),

  restoreAttachment: (id: string, attachmentId: string) =>
    api<void>(`${BASE}/${id}/attachments/${attachmentId}/restore`, { method: 'POST' }),
};

const CHANNELS = '/api/press-release-channels';

/** Public Relations › Settings › Channels. */
export const pressReleaseChannelsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<PressReleaseChannelListItem>>(`${CHANNELS}${serialize(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<PressReleaseChannelListItem>(`${CHANNELS}/${id}`, { signal }),

  create: (body: PressReleaseChannelRequest) => api<PressReleaseChannelListItem>(CHANNELS, { method: 'POST', body }),

  update: (id: string, body: PressReleaseChannelRequest) =>
    api<PressReleaseChannelListItem>(`${CHANNELS}/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`${CHANNELS}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${CHANNELS}/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>(`${CHANNELS}/clear-data`, { method: 'POST' }),
};
